import { RATING_POLARITIES, isRatingUnitId } from './questionnaire/registry';
import { RATING_POLICIES } from './reporting/rating-policy.generated';
import type { RatingStatistics as GeneratedRatingStatistics } from './reporting/canonical-analysis-output.generated';

/**
 * Exact, direction-aware groups computed from accepted raw rating answers.
 * The generated projection of `RatingStatistics` in
 * apps/python-api/models/questionnaire/contracts.py (it was a hand copy of
 * the same type).
 */
export type RatingStatistics = GeneratedRatingStatistics;

const BOX_FIELDS = ['boxSize', 'topBoxesPoints', 'bottomBoxesPoints', 'topBoxesCount', 'bottomBoxesCount', 'topBoxesPercent', 'bottomBoxesPercent'] as const;

/**
 * N of Top N / Bottom N for a discrete scale of `points` points, or null (no
 * box view): 4 to 7 points -> 2, 10 or 11 -> 3. Mirrors Python's
 * `box_size_for_points` (models/questionnaire/rating_scales.py).
 */
function boxSizeForPoints(points: number): number | null {
  if (points >= 4 && points <= 7) return 2;
  if (points >= 10 && points <= 11) return 3;
  return null;
}

/** 0-100 counts its five display bands (`rating_bucket_ids` in Python). */
const ZERO_TO_HUNDRED_BANDS = 5;

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isShare(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100;
}

function isPresent(value: unknown): boolean {
  return value !== undefined && value !== null;
}

/** Adjacent points inside the scale, ascending, `size` of them. */
function isPointRun(value: unknown, size: number, low: number, high: number): value is Array<number> {
  return Array.isArray(value) && value.length === size
    && value.every((point, index) => Number.isInteger(point) && point >= low && point <= high
      && (index === 0 || point === value[index - 1] + 1));
}

/**
 * Decision 20's optional fields, admitted by the same checks as the Python
 * `RatingStatistics.validate_boxes` (only admission: Python computes them).
 * `counts` + `fractionalCount` come together (one count per whole point, per
 * display band on 0-100, summing with the fractions to the base); a box view
 * comes as a set on discrete scales only (boxSize from the point count),
 * both ends at the two scale ends and not overlapping,
 * each box count its points' counts plus fractions inside its range, each
 * share its count over the base. `excludedCount` stands alone;
 * `notApplicableCount` is part of it.
 */
function hasValidCountsAndBoxes(item: Record<string, unknown>, baseSize: number): boolean {
  if (isPresent(item.excludedCount) && !isCount(item.excludedCount)) return false;
  const notApplicable = item.notApplicableCount;
  const excluded = isCount(item.excludedCount) ? item.excludedCount : 0;
  if (isPresent(notApplicable) && !(isCount(notApplicable) && notApplicable <= excluded)) return false;
  const box = BOX_FIELDS.map((key) => item[key]);
  if (!isPresent(item.counts) && !isPresent(item.fractionalCount)) return !box.some(isPresent);
  if (item.kind !== 'rating' || typeof item.unit !== 'string') return false;
  const policy = RATING_POLICIES[item.unit];
  const counts = item.counts;
  const fractional = item.fractionalCount;
  if (!policy || !Array.isArray(counts) || !counts.every(isCount) || !isCount(fractional)) return false;
  const low = policy.minimum;
  const high = policy.maximum;
  const isBanded = item.unit === 'rating_0_100';
  const slots = isBanded ? ZERO_TO_HUNDRED_BANDS : high - low + 1;
  if (counts.length !== slots || counts.reduce((sum, count) => sum + count, 0) + fractional !== baseSize) return false;
  if (!box.some(isPresent)) return true;
  const size = item.boxSize;
  if (size !== 2 && size !== 3) return false;
  // Follows the scale's point count, so 0-100 (101 points) has no box view.
  if (size !== boxSizeForPoints(high - low + 1)) return false;
  const top = item.topBoxesPoints;
  const bottom = item.bottomBoxesPoints;
  if (!isPointRun(top, size, low, high) || !isPointRun(bottom, size, low, high)) return false;
  const starts = [top[0], bottom[0]].sort((first, second) => first - second);
  if (starts[0] !== low || starts[1] !== high - size + 1) return false;
  if (top.some((point) => bottom.includes(point))) return false;
  const boxCounts: Array<[unknown, Array<number>]> = [[item.topBoxesCount, top], [item.bottomBoxesCount, bottom]];
  for (const [boxCount, points] of boxCounts) {
    if (!isCount(boxCount)) return false;
    const atPoints = points.reduce((sum, point) => sum + counts[point - low], 0);
    if (boxCount < atPoints || boxCount > atPoints + fractional) return false;
  }
  if (baseSize === 0) return !isPresent(item.topBoxesPercent) && !isPresent(item.bottomBoxesPercent);
  if (!isShare(item.topBoxesPercent) || !isShare(item.bottomBoxesPercent)) return false;
  const shares: Array<[number, unknown]> = [[item.topBoxesPercent, item.topBoxesCount], [item.bottomBoxesPercent, item.bottomBoxesCount]];
  if (shares.some(([share, count]) => Math.abs(share - (100 * (count as number)) / baseSize) > 0.000001)) return false;
  return item.topBoxesPercent + item.bottomBoxesPercent <= 100 + 0.00001;
}

/** The same admission rules as the Python RatingStatistics wire model. */
export function isRatingStatistics(value: unknown): value is RatingStatistics {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  if (item.policyVersion !== 'rating-statistics.v1' || typeof item.unit !== 'string' || !isRatingUnitId(item.unit)) return false;
  if (typeof item.kind !== 'string' || !['rating', 'nps', 'binary'].includes(item.kind) || !RATING_POLARITIES.includes(item.polarity as never)) return false;
  if (item.kind === 'nps' && item.unit !== 'rating_0_10') return false;
  if ((item.kind === 'binary') !== (item.unit === 'binary_yes_no')) return false;
  if (item.kind !== 'rating' && item.polarity !== 'higher_is_better') return false;
  if (typeof item.baseSize !== 'number' || !Number.isInteger(item.baseSize) || item.baseSize < 0) return false;
  const labels = item.labels as Record<string, unknown> | undefined;
  if (!labels || Array.isArray(labels) || !['favorable', 'middle', 'unfavorable'].every(
    (key) => typeof labels[key] === 'string' && (labels[key] as string).trim().length > 0,
  )) return false;
  if (!hasValidCountsAndBoxes(item, item.baseSize)) return false;
  // Forward-only sample standard deviation (SS-6): a finite non-negative
  // number, only on rating reads of two or more answers.
  if (isPresent(item.standardDeviation)) {
    const deviation = item.standardDeviation;
    if (typeof deviation !== 'number' || !Number.isFinite(deviation) || deviation < 0 || item.baseSize < 2 || item.kind !== 'rating') return false;
  }
  // Forward-only mean in scale points: a finite number inside the scale, only
  // on rating reads of one or more answers.
  if (isPresent(item.mean)) {
    const mean = item.mean;
    const policy = RATING_POLICIES[item.unit];
    if (typeof mean !== 'number' || !Number.isFinite(mean) || item.baseSize < 1 || item.kind !== 'rating') return false;
    if (policy && (mean < policy.minimum || mean > policy.maximum)) return false;
  }
  const values = [item.favorablePercent, item.middlePercent, item.unfavorablePercent];
  if (item.baseSize === 0) return values.every((share) => share === null);
  return values.every((share) => typeof share === 'number' && Number.isFinite(share) && share >= 0 && share <= 100)
    && Math.abs((values as number[]).reduce((sum, share) => sum + share, 0) - 100) <= 0.00001;
}
